/**
 * The Shader node's audio input, analysed on the host.
 *
 * The sandbox cannot reach the host's audio, so the host plays the file through
 * a Web Audio graph — element → analyser → gain → speakers — and posts the
 * analyser's frequency and waveform data to the shader about 30 times a second.
 *
 * The file is read into a same-origin blob first: a MediaElementAudioSourceNode
 * fed from a cross-origin URL without CORS outputs silence, which would leave a
 * shader that "hears nothing" with no error to explain why.
 *
 * Every Shader node fed by the same file shares one player (one element, one
 * analyser, one copy of the sound) instead of each playing its own, out of
 * step. Each node subscribes with its own pause and mute: the track plays while
 * any subscriber runs, and is heard while any running subscriber is unmuted.
 */

import { AUDIO_BIN_COUNT, AUDIO_FFT_SIZE } from './contract'
import type { ShaderAudioFrame } from './contract'
import { buildAudioFrame, createFrameThrottle, createSilentAudioFrame } from './audio-frame'
import { abortError, fetchMediaBlob, throwIfAborted } from './fetch-media'

const MAX_AUDIO_BYTES = 128 * 1024 * 1024

let sharedContext: AudioContext | null = null

/** One AudioContext for the whole app: browsers cap how many a page may open. */
export function getSharedAudioContext(): AudioContext {
  if (!sharedContext || sharedContext.state === 'closed') {
    const Context = window.AudioContext ?? (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    if (!Context) throw new Error('This browser cannot analyse audio.')
    sharedContext = new Context()
  }
  return sharedContext
}

export interface ShaderAudioSourceOptions {
  signal?: AbortSignal
  paused?: boolean
  muted?: boolean
  /** Called about 30 times a second while playing. */
  onFrame: (frame: ShaderAudioFrame) => void
  /** The browser is holding playback until the user interacts (autoplay policy), or has released it. */
  onNeedsGestureChange?: (needsGesture: boolean) => void
  onError?: (error: Error) => void
}

export interface ShaderAudioSource {
  readonly needsGesture: boolean
  /** The last frame sent, or silence before the first one. */
  latestFrame(): ShaderAudioFrame
  setPaused(paused: boolean): void
  /** Silences the speakers (unless another node playing the file is unmuted); analysis continues. */
  setMuted(muted: boolean): void
  /** Rewind to the start, for every node playing the file. */
  restart(): void
  /** Start playback held by the autoplay policy. Call from a click or key handler. */
  resume(): Promise<void>
  dispose(): void
}

interface SharedLoad {
  readonly controller: AbortController
  /** Subscribers waiting for the download or attached to the player. */
  users: number
  player: AudioPlayer | null
  ready: Promise<AudioPlayer>
}

const loads = new Map<string, SharedLoad>()

/**
 * Subscribe to an audio file, downloading and starting it unless another node already has.
 * Rejects with an AbortError if `signal` fires first.
 */
export async function createShaderAudioSource(url: string, options: ShaderAudioSourceOptions): Promise<ShaderAudioSource> {
  throwIfAborted(options.signal)
  const load = acquire(url)
  let player: AudioPlayer
  try {
    player = await untilAborted(load.ready, options.signal)
  } catch (error) {
    release(url, load)
    throw error
  }
  const subscription = new Subscription(player, options, () => release(url, load))
  player.add(subscription)
  return subscription
}

function acquire(url: string): SharedLoad {
  const existing = loads.get(url)
  if (existing) {
    existing.users++
    return existing
  }
  const controller = new AbortController()
  const ready = openPlayer(url, controller.signal).then((player) => {
    if (controller.signal.aborted) {
      player.dispose()
      throw abortError()
    }
    load.player = player
    return player
  })
  const load: SharedLoad = { controller, users: 1, player: null, ready }
  // A failed download is not kept: the next subscriber tries again.
  ready.catch(() => { if (loads.get(url) === load) loads.delete(url) })
  loads.set(url, load)
  return load
}

/** The last subscriber out stops the download or the player. */
function release(url: string, load: SharedLoad) {
  load.users--
  if (load.users > 0) return
  if (loads.get(url) === load) loads.delete(url)
  load.controller.abort()
  load.player?.dispose()
}

/** `promise`, or an AbortError as soon as `signal` fires. */
function untilAborted<T>(promise: Promise<T>, signal: AbortSignal | undefined): Promise<T> {
  if (!signal) return promise
  return new Promise<T>((resolve, reject) => {
    const onAbort = () => reject(abortError())
    if (signal.aborted) { onAbort(); return }
    signal.addEventListener('abort', onAbort, { once: true })
    promise.then(
      (value) => {
        signal.removeEventListener('abort', onAbort)
        if (signal.aborted) onAbort()
        else resolve(value)
      },
      (error: unknown) => {
        signal.removeEventListener('abort', onAbort)
        reject(error)
      },
    )
  })
}

async function openPlayer(url: string, signal: AbortSignal): Promise<AudioPlayer> {
  const blob = await fetchMediaBlob(url, { signal, maxBytes: MAX_AUDIO_BYTES })
  throwIfAborted(signal)
  const objectUrl = URL.createObjectURL(blob)
  const element = new Audio()
  element.loop = true
  element.preload = 'auto'
  element.src = objectUrl
  try {
    await waitForMetadata(element, signal)
    return new AudioPlayer(getSharedAudioContext(), element, objectUrl)
  } catch (error) {
    element.removeAttribute('src')
    element.load()
    URL.revokeObjectURL(objectUrl)
    throw error
  }
}

function waitForMetadata(element: HTMLAudioElement, signal: AbortSignal | undefined): Promise<void> {
  return new Promise((resolve, reject) => {
    const cleanup = () => {
      element.removeEventListener('loadedmetadata', onLoaded)
      element.removeEventListener('error', onError)
      signal?.removeEventListener('abort', onAbort)
    }
    const onLoaded = () => { cleanup(); resolve() }
    const onError = () => { cleanup(); reject(new Error('This audio format cannot be played in this browser.')) }
    const onAbort = () => { cleanup(); reject(abortError()) }
    if (element.readyState >= HTMLMediaElement.HAVE_METADATA) { resolve(); return }
    element.addEventListener('loadedmetadata', onLoaded)
    element.addEventListener('error', onError)
    signal?.addEventListener('abort', onAbort)
  })
}

/** One node's view of a shared player: its own pause, mute and callbacks. */
class Subscription implements ShaderAudioSource {
  paused: boolean
  muted: boolean
  private gestureShown = false
  private disposed = false

  constructor(
    private readonly player: AudioPlayer,
    readonly options: ShaderAudioSourceOptions,
    private readonly onDispose: () => void,
  ) {
    this.paused = options.paused === true
    this.muted = options.muted === true
  }

  get needsGesture(): boolean {
    return !this.disposed && !this.paused && this.player.needsGesture
  }

  latestFrame(): ShaderAudioFrame {
    return this.player.latestFrame()
  }

  setPaused(paused: boolean): void {
    if (this.disposed || paused === this.paused) return
    this.paused = paused
    this.player.sync()
  }

  setMuted(muted: boolean): void {
    if (this.disposed || muted === this.muted) return
    this.muted = muted
    this.player.sync()
  }

  restart(): void {
    if (!this.disposed) this.player.restart()
  }

  resume(): Promise<void> {
    return this.disposed ? Promise.resolve() : this.player.resume()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.player.remove(this)
    this.onDispose()
  }

  /** Tell the node when its view of the autoplay hold changes. */
  notifyGesture(): void {
    const needed = this.needsGesture
    if (needed === this.gestureShown) return
    this.gestureShown = needed
    this.options.onNeedsGestureChange?.(needed)
  }
}

class AudioPlayer {
  private readonly subscribers = new Set<Subscription>()
  private readonly source: MediaElementAudioSourceNode
  private readonly analyser: AnalyserNode
  private readonly gain: GainNode
  private readonly fftBuffer = new Float32Array(AUDIO_BIN_COUNT)
  private readonly waveformBuffer = new Float32Array(AUDIO_BIN_COUNT)
  private readonly shouldEmit = createFrameThrottle()
  private frame = createSilentAudioFrame()
  private playing = false
  private playBlocked = false
  private gestureNeeded = false
  private disposed = false
  private raf = 0

  constructor(
    private readonly context: AudioContext,
    private readonly element: HTMLAudioElement,
    private readonly objectUrl: string,
  ) {
    this.source = context.createMediaElementSource(element)
    this.analyser = context.createAnalyser()
    this.analyser.fftSize = AUDIO_FFT_SIZE
    this.gain = context.createGain()
    this.gain.gain.value = 0
    this.source.connect(this.analyser)
    this.analyser.connect(this.gain)
    this.gain.connect(context.destination)
    context.addEventListener('statechange', this.updateGesture)
  }

  get needsGesture(): boolean {
    return this.gestureNeeded
  }

  latestFrame(): ShaderAudioFrame {
    return this.frame
  }

  add(subscription: Subscription): void {
    this.subscribers.add(subscription)
    this.sync()
  }

  remove(subscription: Subscription): void {
    this.subscribers.delete(subscription)
    this.sync()
  }

  /** Play while any subscriber runs; be heard while any running subscriber is unmuted. */
  sync(): void {
    if (this.disposed) return
    let running = false
    let audible = false
    for (const subscriber of this.subscribers) {
      if (subscriber.paused) continue
      running = true
      if (!subscriber.muted) audible = true
    }
    this.gain.gain.value = audible ? 1 : 0
    if (running && !this.playing) {
      this.playing = true
      this.play()
    } else if (!running && this.playing) {
      this.playing = false
      this.element.pause()
      this.stopLoop()
    }
    this.updateGesture()
  }

  restart(): void {
    if (!this.disposed) this.element.currentTime = 0
  }

  async resume(): Promise<void> {
    if (this.disposed) return
    // Both calls start synchronously, inside the user's gesture, before anything is awaited.
    const contextResumed = this.context.state === 'running' ? Promise.resolve() : this.context.resume()
    const played = this.playing ? this.element.play() : Promise.resolve()
    try {
      await Promise.all([contextResumed, played])
      this.playBlocked = false
    } catch {
      // Still held; needsGesture stays true so the UI keeps offering the button.
    }
    this.updateGesture()
  }

  dispose(): void {
    if (this.disposed) return
    this.disposed = true
    this.playing = false
    this.stopLoop()
    this.context.removeEventListener('statechange', this.updateGesture)
    this.element.pause()
    this.element.removeAttribute('src')
    this.element.load()
    this.source.disconnect()
    this.analyser.disconnect()
    this.gain.disconnect()
    URL.revokeObjectURL(this.objectUrl)
    this.subscribers.clear()
  }

  private play() {
    this.startLoop()
    // Created outside a gesture, the shared context may start suspended. Ask once; if the
    // browser refuses, the UI shows a button that calls resume() from a real click.
    if (this.context.state === 'suspended') this.context.resume().catch(() => undefined)
    this.element.play().then(
      () => {
        this.playBlocked = false
        this.updateGesture()
      },
      (error: unknown) => {
        if (this.disposed) return
        if (error instanceof DOMException && error.name === 'NotAllowedError') this.playBlocked = true
        else if (!(error instanceof DOMException && error.name === 'AbortError')) {
          const failure = new Error('The audio could not be played.')
          for (const subscriber of this.subscribers) subscriber.options.onError?.(failure)
        }
        this.updateGesture()
      },
    )
  }

  private readonly updateGesture = () => {
    this.gestureNeeded = !this.disposed && this.playing && (this.playBlocked || this.context.state === 'suspended')
    for (const subscriber of this.subscribers) subscriber.notifyGesture()
  }

  private startLoop() {
    if (!this.raf) this.raf = requestAnimationFrame(this.tick)
  }

  private stopLoop() {
    if (this.raf) cancelAnimationFrame(this.raf)
    this.raf = 0
  }

  private readonly tick = (now: number) => {
    this.raf = 0
    if (this.disposed || !this.playing) return
    if (this.shouldEmit(now)) {
      this.analyser.getFloatFrequencyData(this.fftBuffer)
      this.analyser.getFloatTimeDomainData(this.waveformBuffer)
      this.frame = buildAudioFrame(this.fftBuffer, this.waveformBuffer)
      for (const subscriber of this.subscribers) if (!subscriber.paused) subscriber.options.onFrame(this.frame)
    }
    this.raf = requestAnimationFrame(this.tick)
  }
}
